import type { SexualHealthState } from "./SexualHealthContext";
import { paths } from "@src/packages/openapi/schema";
import { RequestBody } from "@src/packages/openapi/helpers";

type PatchPayload = RequestBody<
  paths["/{tenantId}/patients/{patientId}"]["patch"]
>;

export function transformSexualHealthStateToRequest(
  state: SexualHealthState,
): PatchPayload {
  const sexualHealth: NonNullable<
    NonNullable<PatchPayload["obGynHistory"]>["sexualHealth"]
  > = {};

  if (state.id) {
    sexualHealth.id = state.id;
  }

  if (state.hadPapSmear !== null) {
    sexualHealth.hadPapSmear = state.hadPapSmear;
  }
  if (state.papSmearResultAbnormal !== null) {
    sexualHealth.papSmearResultAbnormal = state.papSmearResultAbnormal;
  }
  if (state.hadPapSmearProcedures !== null) {
    sexualHealth.hadPapSmearProcedures = state.hadPapSmearProcedures;
  }
  if (state.hadSTDs !== null) {
    sexualHealth.hadSTDs = state.hadSTDs;
  }
  if (state.hasVaginalInfectionHistory !== null) {
    sexualHealth.hasVaginalInfectionHistory = state.hasVaginalInfectionHistory;
  }
  if (state.hasPainfulIntercourse !== null) {
    sexualHealth.hasPainfulIntercourse = state.hasPainfulIntercourse;
  }
  if (state.painfulIntercourseScale !== null) {
    sexualHealth.painfulIntercourseScale = state.painfulIntercourseScale;
  }
  if (state.usesLubricant !== null) {
    sexualHealth.usesLubricant = state.usesLubricant;
  }
  if (state.lubricantName !== null) {
    sexualHealth.lubricantName = state.lubricantName;
  }
  if (state.lubricantUseFrequency !== null) {
    sexualHealth.lubricantUseFrequency = state.lubricantUseFrequency;
  }
  if (state.isPartnerEjaculatingInVagina !== null) {
    sexualHealth.isPartnerEjaculatingInVagina =
      state.isPartnerEjaculatingInVagina;
  }
  if (state.vaginalEjaculationFrequency !== null) {
    sexualHealth.vaginalEjaculationFrequency =
      state.vaginalEjaculationFrequency;
  }
  // Clear fields when user says no to painful intercourse
  if (state.hasPainfulIntercourse === false) {
    sexualHealth.painfulIntercourseScale = null;
  }
  // Clear lubricant details when user says no
  if (state.usesLubricant === false) {
    sexualHealth.lubricantName = null;
    sexualHealth.lubricantUseFrequency = null;
  }
  // Clear ejaculation frequency when user says no
  if (state.isPartnerEjaculatingInVagina === false) {
    sexualHealth.vaginalEjaculationFrequency = null;
  }
  // A normal Pap result or a change to "no Pap smear" makes follow-up
  // procedures inapplicable. Clear the persisted answer explicitly because
  // the state intentionally uses null for an unanswered conditional field.
  if (state.hadPapSmear === false || state.papSmearResultAbnormal === false) {
    sexualHealth.papSmearResultAbnormal =
      state.hadPapSmear === false ? null : false;
    sexualHealth.hadPapSmearProcedures = null;
  }

  // ── Pap smear procedures ──
  type ProcedureEntry = {
    id?: string;
    procedure: "colposcopy" | "laser_treatment" | "cryotherapy" | "other";
    other?: string | null;
    procedureDate?: string | null;
    notes?: string | null;
    op?: "delete";
  };

  const proceduresPayload: ProcedureEntry[] = [];
  const papProcedureBranchIsInapplicable =
    state.hadPapSmear === false || state.papSmearResultAbnormal === false;

  // Deletions
  state.procedureIdsToDelete.forEach((id) => {
    if (id) {
      proceduresPayload.push({ id, procedure: "other", op: "delete" });
    }
  });

  // Add/update
  if (state.hadPapSmearProcedures === true) {
    const deleteIdSet = new Set(state.procedureIdsToDelete);
    state.papSmearProcedures
      .filter((p) => !p.id || !deleteIdSet.has(p.id))
      .forEach((p) => {
        const entry: ProcedureEntry = { procedure: p.procedure };
        if (p.id) entry.id = p.id;
        if (p.other != null) entry.other = p.other;
        if (p.procedureDate != null) entry.procedureDate = p.procedureDate;
        if (p.notes != null) entry.notes = p.notes;
        proceduresPayload.push(entry);
      });
  } else if (
    state.hadPapSmearProcedures === false ||
    papProcedureBranchIsInapplicable
  ) {
    // Delete all existing when the direct answer is None, the patient answers
    // No to a Pap smear, or the Pap result is normal.
    const deleteIdSet = new Set(state.procedureIdsToDelete);
    state.papSmearProcedures.forEach((p) => {
      if (p.id && !deleteIdSet.has(p.id)) {
        proceduresPayload.push({
          id: p.id,
          procedure: p.procedure,
          op: "delete",
        });
      }
    });
  }

  if (proceduresPayload.length > 0) {
    sexualHealth.papSmearProcedures = proceduresPayload;
  }

  // ── STDs ──
  type STDEntry = {
    id?: string;
    stdType:
      | "syphilis"
      | "gonorrhea"
      | "chlamydia"
      | "hpv"
      | "hepatitis"
      | "hiv"
      | "genital_herpes"
      | "trichomonas_vaginalis"
      | "other";
    other?: string | null;
    dateOfDiagnosis?: string | null;
    treatment?: string | null;
    notes?: string | null;
    op?: "delete";
  };

  const stdsPayload: STDEntry[] = [];

  state.stdIdsToDelete.forEach((id) => {
    if (id) {
      stdsPayload.push({ id, stdType: "other", op: "delete" });
    }
  });

  if (state.hadSTDs === true) {
    const deleteIdSet = new Set(state.stdIdsToDelete);
    state.stds
      .filter((s) => !s.id || !deleteIdSet.has(s.id))
      .forEach((s) => {
        const entry: STDEntry = { stdType: s.stdType };
        if (s.id) {
          entry.id = s.id;
        }
        if (s.other != null) {
          entry.other = s.other;
        }
        if (s.dateOfDiagnosis != null) {
          entry.dateOfDiagnosis = s.dateOfDiagnosis;
        }
        if (s.treatment != null) {
          entry.treatment = s.treatment;
        }
        if (s.notes != null) {
          entry.notes = s.notes;
        }
        stdsPayload.push(entry);
      });
  } else if (state.hadSTDs === false) {
    const deleteIdSet = new Set(state.stdIdsToDelete);
    state.stds.forEach((s) => {
      if (s.id && !deleteIdSet.has(s.id)) {
        stdsPayload.push({ id: s.id, stdType: s.stdType, op: "delete" });
      }
    });
  }

  if (stdsPayload.length > 0) {
    sexualHealth.stds = stdsPayload;
  }

  // ── Vaginal infection histories ──
  type InfectionEntry = {
    id?: string;
    vaginalInfectionHistory: "often" | "past" | "none";
    description?: string | null;
    startDate?: string | null;
    notes?: string | null;
    op?: "delete";
  };

  const infectionsPayload: InfectionEntry[] = [];

  state.infectionIdsToDelete.forEach((id) => {
    if (id) {
      infectionsPayload.push({
        id,
        vaginalInfectionHistory: "none",
        op: "delete",
      });
    }
  });

  if (state.hasVaginalInfectionHistory === true) {
    const deleteIdSet = new Set(state.infectionIdsToDelete);
    state.vaginalInfectionHistories
      .filter((v) => !v.id || !deleteIdSet.has(v.id))
      .forEach((v) => {
        const entry: InfectionEntry = {
          vaginalInfectionHistory: v.vaginalInfectionHistory,
        };
        if (v.id) entry.id = v.id;
        if (v.description != null) entry.description = v.description;
        if (v.startDate != null) entry.startDate = v.startDate;
        if (v.notes != null) entry.notes = v.notes;
        infectionsPayload.push(entry);
      });
  } else if (state.hasVaginalInfectionHistory === false) {
    const deleteIdSet = new Set(state.infectionIdsToDelete);
    state.vaginalInfectionHistories.forEach((v) => {
      if (v.id && !deleteIdSet.has(v.id)) {
        infectionsPayload.push({
          id: v.id,
          vaginalInfectionHistory: v.vaginalInfectionHistory,
          op: "delete",
        });
      }
    });
  }

  if (infectionsPayload.length > 0) {
    sexualHealth.vaginalInfectionHistories = infectionsPayload;
  }

  const result: PatchPayload = {};

  if (Object.keys(sexualHealth).length > 0) {
    result.obGynHistory = { sexualHealth };
  }

  if (
    state.userGender === "male" &&
    state.hasDifficultiesWithErection !== null
  ) {
    result.maleHistory = {
      sexualHealth: {
        ...(state.maleHistorySexualHealthId
          ? { id: state.maleHistorySexualHealthId }
          : {}),
        hasDifficultiesWithErection: state.hasDifficultiesWithErection,
      },
    };
  }

  return result;
}
